/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import auMap from "@/lib/australiaMap.json";
import { PURE_SOULS_URL } from "@/lib/site";
import { PROVIDER_GEO } from "@/lib/providerGeo";
import { PawShapes, Paw } from "@/components/site/Motifs";
import CountUp from "@/components/site/CountUp";

/**
 * AustraliaMap — a calm provider locator. Each verified provider is a paw
 * marker on a softly tinted map of Australia, synced to a list on the side.
 * Marker positions are projected from real latitude/longitude, so adding a
 * provider only needs coordinates.
 */

// Calibrated bounds of the inline SVG (equirectangular fit to the artwork).
const B = {
  xmin: 6.99, xmax: 278.94, ymin: 5.29, ymax: 257.01,
  lonMin: 113.15, lonMax: 153.64, latMax: -10.69, latMin: -43.64,
};
const project = (lat, lng) => ({
  x: B.xmin + ((lng - B.lonMin) / (B.lonMax - B.lonMin)) * (B.xmax - B.xmin),
  y: B.ymin + ((B.latMax - lat) / (B.latMax - B.latMin)) * (B.ymax - B.ymin),
});

const STATE_NAMES = {
  QLD: "Queensland", NSW: "New South Wales", VIC: "Victoria", SA: "South Australia",
  WA: "Western Australia", TAS: "Tasmania", NT: "Northern Territory", ACT: "Australian Capital Territory",
};

function stateOf(id) {
  for (const code of ["nsw", "vic", "qld", "sa", "wa", "nt", "tas", "act"]) {
    if (id === code || id.startsWith(code + "-")) return code.toUpperCase();
  }
  return null;
}

export default function AustraliaMap({ providers = [], focusName = null }) {
  const [active, setActive] = useState(null); // provider name (selected)
  const [hover, setHover] = useState(null); // provider name (hovered)

  useEffect(() => {
    if (focusName) setActive(focusName);
  }, [focusName]);

  const markers = useMemo(
    () =>
      providers
        .filter((p) => PROVIDER_GEO[p.name])
        .map((p) => ({ ...p, ...PROVIDER_GEO[p.name], ...project(PROVIDER_GEO[p.name].lat, PROVIDER_GEO[p.name].lng) })),
    [providers]
  );

  const statesWithProviders = useMemo(() => new Set(providers.map((p) => p.state)), [providers]);
  const cityCount = markers.length;
  const stateCount = statesWithProviders.size;

  const current = hover || active;
  const selectedProvider = markers.find((m) => m.name === active);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start" data-testid="australia-map">
      {/* Map */}
      <div className="lg:col-span-7">
        <div className="relative rounded-3xl border border-sand-300/70 bg-gradient-to-b from-white to-sand-50 shadow-card p-4 sm:p-8">
          <svg
            viewBox={auMap.viewBox}
            className="w-full h-auto overflow-visible"
            role="img"
            aria-label="Map of Australia showing verified water cremation provider locations"
          >
            {/* Land */}
            {auMap.locations.map((loc) => {
              const code = stateOf(loc.id);
              const has = statesWithProviders.has(code);
              return (
                <path
                  key={loc.id}
                  d={loc.path}
                  data-testid={`map-state-${code}-${loc.id}`}
                  style={{
                    fill: has ? "#E4EBE0" : "#EFEFE9",
                    stroke: "#FFFFFF",
                    strokeWidth: 0.7,
                  }}
                />
              );
            })}

            {/* Provider paw markers */}
            {markers.map((m, i) => {
              const isActive = current === m.name;
              const anchor = m.x > 232 ? "end" : m.x < 42 ? "start" : "middle";
              const dx = anchor === "end" ? -7 : anchor === "start" ? 7 : 0;
              return (
                <g
                  key={m.id}
                  transform={`translate(${m.x} ${m.y})`}
                  className="cursor-pointer"
                  style={{ animation: "marker-fade 0.5s ease-out both", animationDelay: `${0.25 + i * 0.07}s` }}
                  onMouseEnter={() => setHover(m.name)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => setActive(m.name)}
                  data-testid={`map-marker-${m.city.toLowerCase()}`}
                >
                  {/* Enlarged invisible touch target */}
                  <circle r="9" fill="transparent" />
                  <g style={{ transformBox: "fill-box", transformOrigin: "center", transform: isActive ? "scale(1.4)" : "scale(1)", transition: "transform 0.3s ease" }}>
                    <circle r="5" fill="#FFFFFF" fillOpacity="0.95" />
                    <circle r="5" fill="none" stroke={isActive ? "#2A3B32" : "#7A8C67"} strokeWidth={isActive ? 0.8 : 0.5} />
                    <g transform="scale(0.62)">
                      <PawShapes fill={isActive ? "#2A3B32" : "#5F6E50"} />
                    </g>
                  </g>
                  {isActive && (
                    <text
                      x={dx}
                      y="-8.5"
                      textAnchor={anchor}
                      className="font-sans"
                      style={{ fontSize: 6, fontWeight: 700, fill: "#2A3B32", paintOrder: "stroke", stroke: "#F9F9F6", strokeWidth: 1.8, strokeLinejoin: "round" }}
                    >
                      {m.city}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-6" data-testid="map-legend">
          <div className="flex items-center gap-2">
            <Paw size={16} fill="#5F6E50" />
            <span className="text-sm text-forest-700">Verified provider</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-sm" style={{ background: "#E4EBE0", border: "1px solid rgba(42,59,50,0.12)" }} />
            <span className="text-sm text-forest-700">Providers available</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-sm" style={{ background: "#EFEFE9", border: "1px solid rgba(42,59,50,0.12)" }} />
            <span className="text-sm text-forest-700">Coming soon</span>
          </div>
        </div>
      </div>

      {/* Side panel: stat + synced provider list */}
      <div className="lg:col-span-5">
        <div className="flex items-end gap-4 pb-6 mb-6 border-b border-sand-300">
          <div className="font-serif text-6xl sm:text-7xl text-forest-900 leading-none tracking-tight">
            <CountUp to={cityCount} />
          </div>
          <div className="pb-1.5">
            <div className="font-serif text-2xl text-forest-900 leading-tight">verified providers</div>
            <div className="text-sm text-forest-600">
              across {cityCount} cities in {stateCount} states
            </div>
          </div>
        </div>

        <div className="space-y-2 max-h-[420px] overflow-y-auto no-scrollbar pr-1" data-testid="map-provider-list">
          {markers.map((m) => {
            const isActive = active === m.name;
            return (
              <div
                key={m.id}
                onMouseEnter={() => setHover(m.name)}
                onMouseLeave={() => setHover(null)}
              >
                <button
                  onClick={() => setActive(isActive ? null : m.name)}
                  data-testid={`map-list-item-${m.city.toLowerCase()}`}
                  className={`w-full text-left flex items-center gap-4 rounded-2xl border px-4 py-3.5 transition-all duration-200 ${
                    current === m.name
                      ? "bg-sage-50 border-sage-200"
                      : "bg-white border-sand-300/70 hover:border-forest-900/20"
                  }`}
                >
                  <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${current === m.name ? "bg-forest-900 text-sand-50" : "bg-sage-50 text-sage-700"}`}>
                    <Paw size={16} fill="currentColor" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-serif text-lg text-forest-900 leading-tight truncate">{m.name}</span>
                    <span className="block text-xs text-forest-500 tracking-wide">{m.city}, {m.state}</span>
                  </span>
                  <ArrowUpRight size={16} className={`shrink-0 transition-transform ${isActive ? "rotate-90" : ""} text-forest-400`} />
                </button>

                {isActive && (
                  <div className="px-4 pt-3 pb-4 animate-fade-in-up" data-testid={`map-detail-${m.city.toLowerCase()}`}>
                    <p className="text-forest-700 text-[15px] leading-relaxed">{m.description}</p>
                    {m.services && m.services.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {m.services.map((s) => (
                          <span key={s} className="text-xs tracking-wide text-sage-700 bg-sage-50 border border-sage-100 rounded-full px-3 py-1">{s}</span>
                        ))}
                      </div>
                    )}
                    <a
                      href={m.website || PURE_SOULS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary mt-4"
                      data-testid={`map-visit-${m.city.toLowerCase()}`}
                    >
                      Visit provider <ArrowUpRight size={15} />
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-forest-500 mt-5 leading-relaxed">
          Tap a paw on the map or a provider above to see details. The Australian Capital
          Territory has no verified provider yet, so it is marked coming soon.
        </p>
      </div>
    </div>
  );
}
